'use client';
import React, { useState, useEffect, useRef } from 'react';

type Theme = 'dark' | 'light' | 'ogre' | 'purple' | 'crazy';

const THEMES: { id: Theme; label: string; emoji?: string }[] = [
  { id: 'dark', label: 'Dark' },
  { id: 'light', label: 'Light' },
  { id: 'ogre', label: 'Ogre' },
  { id: 'purple', label: 'Purple' },
  { id: 'crazy', label: 'Crazy' },
];

function randomHex(): string {
  return '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
}

function randomRgba(alpha = 0.25): string {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `rgba(${r},${g},${b},${alpha})`;
}

function applyCrazyColors() {
  const root = document.documentElement;
  root.style.setProperty('--bg-base', randomHex());
  root.style.setProperty('--accent-color', randomHex());
  root.style.setProperty('--accent-hover', randomHex());
  root.style.setProperty('--accent-subtle', randomRgba(0.25));
  root.style.setProperty('--primary-text', randomHex());
  root.style.setProperty('--muted-text', randomHex());
  root.style.setProperty('--panel-bg', randomHex());
  root.style.setProperty('--panel-border', randomHex());
  root.style.setProperty('--crazy-wordmark-color1', randomHex());
  root.style.setProperty('--crazy-wordmark-color2', randomHex());
  root.style.setProperty('--crazy-logo-hue', String(Math.floor(Math.random() * 360)));
}

function clearCrazyColors() {
  const root = document.documentElement;
  const vars = ['--bg-base','--accent-color','--accent-hover','--accent-subtle','--primary-text','--muted-text','--panel-bg','--panel-border'];
  vars.forEach(v => root.style.removeProperty(v));
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
}

export default function ThemeSwitcher() {
  const [current, setCurrent] = useState<Theme>('dark');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const saved = (localStorage.getItem('giga-theme') as Theme) || 'dark';
    setCurrent(saved);
    applyTheme(saved);
    if (saved === 'crazy') {
      applyCrazyColors();
      intervalRef.current = setInterval(applyCrazyColors, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function selectTheme(theme: Theme) {
    // Stop any existing crazy interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // Clear inline crazy overrides if switching away
    if (theme !== 'crazy') {
      clearCrazyColors();
    }

    setCurrent(theme);
    localStorage.setItem('giga-theme', theme);
    applyTheme(theme);

    if (theme === 'crazy') {
      applyCrazyColors();
      intervalRef.current = setInterval(applyCrazyColors, 5000);
    }

    setOpen(false);
  }

  return (
    <div ref={ref} className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
      {open && (
        <div className="theme-panel mb-1 rounded-2xl border shadow-2xl overflow-hidden w-40" style={{ background: 'var(--panel-bg)', borderColor: 'var(--panel-border)' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--panel-border)' }}>
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-text)' }}>Theme</p>
          </div>
          {THEMES.map(theme => (
            <button
              key={theme.id}
              onClick={() => selectTheme(theme.id)}
              className="w-full flex items-center gap-3 px-4 py-3 transition-colors duration-150 text-left"
              style={{
                background: current === theme.id ? 'var(--accent-subtle)' : 'transparent',
                color: 'var(--primary-text)',
              }}
            >
              <p className="text-sm font-medium leading-tight flex-1">
                {theme.emoji ? `${theme.emoji} ${theme.label}` : theme.label}
              </p>
              {current === theme.id && (
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{
                    background: 'var(--accent-color)',
                    opacity: current === 'light' ? 0.35 : 1,
                  }}
                />
              )}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all duration-150 active:scale-95 border"
        style={{ background: 'var(--panel-bg)', borderColor: 'var(--panel-border)', color: 'var(--primary-text)' }}
        title="Switch theme"
        aria-label="Switch theme"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4"/>
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
        </svg>
      </button>
    </div>
  );
}
